import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import {
  serverTimestamp,
  addDoc,
  collection,
  doc,
  getDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export default function RefundTool() {
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [reason, setReason] = useState('');
  const [actionType, setActionType] = useState('refund'); // 'refund' or 'cancel' or 'refundDeposit'
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleAction = async () => {
    if (!paymentIntentId || !reason) {
      alert(`Please enter both ${actionType === 'refundDeposit' ? 'Booking ID' : 'PaymentIntent ID'} and reason.`);
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      let fn;
      if (actionType === 'refundDeposit') {
        fn = httpsCallable(functions, 'manualDepositRefund');
      } else if (actionType === 'cancel') {
        fn = httpsCallable(functions, 'cancelStripeRefund');
      } else {
        fn = httpsCallable(functions, 'manualRefund');
      }

      const result = await fn(
        actionType === 'refundDeposit'
          ? { bookingId: paymentIntentId, reason }
          : { paymentIntentId, reason }
      );

      const refund = result?.data?.refund || result?.data?.deposit || {};
      const userId = result?.data?.uid || null;
      const bookingId = result?.data?.bookingId || null;

      const localAdmin = JSON.parse(localStorage.getItem('user')) || {};
      const adminInfo = {
        email: localAdmin.email || 'unknown',
        uid: localAdmin.uid || 'unknown',
        name: localAdmin.name || 'Admin',
        role: localAdmin.role || 'admin',
      };

      let affectedUser = {
        uid: userId || 'unknown',
        email: null,
        name: null,
      };
      if (userId) {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const user = userDoc.data();
          affectedUser.email = user.email || null;
          affectedUser.name = user.name || user.displayName || null;
        }
      }

      const logData = {
        paymentIntentId,
        bookingId,
        reason,
        refund: {
          id: refund.id,
          amount: refund.amount ? refund.amount / 100 : null,
          currency: refund.currency,
          chargeId: refund.charge,
          status: refund.status,
        },
        affectedUser,
        triggeredBy: adminInfo,
        action: actionType,
        timestamp: serverTimestamp(),
        isoTime: new Date().toISOString(),
      };

      await addDoc(collection(db, 'refundLogs'), logData);
      if (userId) {
        await addDoc(collection(db, `users/${userId}/refundHistory`), logData);
      }

      setMessage(`✅ ${actionType === 'cancel' ? 'Refund Canceled' : 'Refund Issued'}: ${logData.refund.amount} ${logData.refund.currency}`);
      setPaymentIntentId('');
      setReason('');
    } catch (err) {
      console.error('❌ Action failed:', err);
      setMessage(`❌ Failed to ${actionType === 'cancel' ? 'cancel refund' : actionType === 'refundDeposit' ? 'refund deposit' : 'process refund'}.`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>💸 Refund / Cancel Tool</h2>

      <div style={styles.toggleRow}>
        <label>
          <input
            type="radio"
            name="actionType"
            value="refund"
            checked={actionType === 'refund'}
            onChange={() => setActionType('refund')}
          />
          Refund
        </label>
        <label style={{ marginLeft: '2rem' }}>
          <input
            type="radio"
            name="actionType"
            value="cancel"
            checked={actionType === 'cancel'}
            onChange={() => setActionType('cancel')}
          />
          Cancel Refund
        </label>
        <label style={{ marginLeft: '2rem' }}>
          <input
            type="radio"
            name="actionType"
            value="refundDeposit"
            checked={actionType === 'refundDeposit'}
            onChange={() => setActionType('refundDeposit')}
          />
          Refund Deposit
        </label>
      </div>

      <input
        type="text"
        placeholder={actionType === 'refundDeposit' ? 'Booking ID' : 'PaymentIntent ID'}
        value={paymentIntentId}
        onChange={e => setPaymentIntentId(e.target.value)}
        style={styles.input}
      />
      <input
        type="text"
        placeholder="Reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        style={styles.input}
      />

      <button
        onClick={handleAction}
        disabled={isProcessing}
        style={styles.submitButton}
      >
        {isProcessing
          ? actionType === 'cancel'
            ? 'Canceling...'
            : actionType === 'refundDeposit'
              ? 'Refunding Deposit...'
              : 'Processing...'
          : actionType === 'cancel'
            ? 'Cancel Refund'
            : actionType === 'refundDeposit'
              ? 'Refund Deposit'
              : 'Submit Refund'}
      </button>

      {message && <p style={styles.message}>{message}</p>}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem',
    fontWeight: 500,
  },
  input: {
    display: 'block',
    width: '100%',
    maxWidth: '400px',
    marginBottom: '1rem',
    padding: '0.5rem',
    fontSize: '1rem',
  },
  submitButton: {
    backgroundColor: '#22c55e',
    color: 'white',
    fontWeight: 'bold',
    padding: '0.5rem 1.5rem',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
  },
  message: {
    marginTop: '1rem',
    fontWeight: 500,
    fontSize: '1rem',
    color: '#065f46',
  },
};
